// SEM-5585 — turning API schema identifiers into words a Turkish HR admin reads.
//
// These screens surface values the platform stores as identifiers: audit action
// codes, table names, target kinds, realm role ids, residency regions. They used
// to be printed straight through, so the Settings screens showed `region_tr`,
// `role_super_admin`, `entity_permission_assignment` and `session.me.fetched`.
//
// The KNOWN sets are derived from the i18n tree rather than listed again here.
// A second hand-written list of codes is exactly how a catalogue and its labels
// drift apart — the list would keep an entry the translation no longer has, and
// the screen would render a raw key as if it were a name.

import type { TFunction } from "i18next";

import { leafPaths } from "@/i18n/leaf-paths";

import { ADMIN_SETTINGS_I18N_EN } from "./i18n-keys";

/** Every audit action this console can name, as dotted codes. */
export const AUDIT_ACTION_CODES: readonly string[] = leafPaths(
  ADMIN_SETTINGS_I18N_EN.admin_settings.audit_action,
);

/** Every entity kind this console can name — the audit filter's option set. */
export const AUDIT_ENTITY_KINDS: readonly string[] = Object.keys(
  ADMIN_SETTINGS_I18N_EN.admin_settings.audit_entity,
);

const KNOWN_ACTIONS = new Set(AUDIT_ACTION_CODES);
const KNOWN_ENTITIES = new Set(AUDIT_ENTITY_KINDS);

export interface Term {
  readonly label: string;
  /**
   * False when the catalogue has no entry. The caller shows the raw identifier
   * marked as a system code — inventing a name for an unrecognised code would
   * be a worse lie than admitting we do not have one.
   */
  readonly known: boolean;
}

export function auditActionTerm(t: TFunction, code: string): Term {
  return KNOWN_ACTIONS.has(code)
    ? { label: t(`admin_settings.audit_action.${code}`), known: true }
    : { label: code, known: false };
}

export function auditEntityTerm(t: TFunction, entityKind: string): Term {
  return KNOWN_ENTITIES.has(entityKind)
    ? { label: t(`admin_settings.audit_entity.${entityKind}`), known: true }
    : { label: entityKind, known: false };
}

/**
 * Notification type slugs, derived the same way as the audit actions: the
 * catalogue on screen comes from the SERVER (the router's policy map), so this
 * side only decides whether it has a name for a slug. A type the server sends
 * and this build cannot name prints as its slug and is badged — the same rule
 * every other identifier on these screens follows.
 */
const KNOWN_NOTIFICATION_TYPES = new Set(
  leafPaths(ADMIN_SETTINGS_I18N_EN.admin_settings.notification_type),
);

export function notificationTypeTerm(t: TFunction, type: string): Term {
  return KNOWN_NOTIFICATION_TYPES.has(type)
    ? { label: t(`admin_settings.notification_type.${type}`), known: true }
    : { label: type, known: false };
}

export function notificationGroupTerm(t: TFunction, group: string): Term {
  const known = group in ADMIN_SETTINGS_I18N_EN.admin_settings.notification_group;
  return known
    ? { label: t(`admin_settings.notification_group.${group}`), known }
    : { label: group, known };
}

/**
 * Channel names as the server sends them (`sms`, `webhook`) turned into the
 * words a person reads. The undeliverable list is a plain string[] off the wire,
 * so before this it was joined and printed raw on both notification screens.
 */
export function notificationChannelTerm(t: TFunction, channel: string): Term {
  const known = channel in ADMIN_SETTINGS_I18N_EN.admin_settings.notification_channel;
  return known
    ? { label: t(`admin_settings.notification_channel.${channel}`), known }
    : { label: channel, known };
}

/** "SMS, anlık bildirim ve WhatsApp": the list's own conjunction comes from CLDR, not from our catalogue. */
export function channelListLabel(
  t: TFunction,
  channels: readonly string[],
  displayLocale: string,
): string {
  return new Intl.ListFormat(displayLocale, { style: "long", type: "conjunction" }).format(
    channels.map((channel) => notificationChannelTerm(t, channel).label),
  );
}

export function targetKindTerm(t: TFunction, targetKind: string): Term {
  const known = targetKind in ADMIN_SETTINGS_I18N_EN.admin_settings.target_kind;
  return known
    ? { label: t(`admin_settings.target_kind.${targetKind}`), known }
    : { label: targetKind, known };
}

export function roleTerm(t: TFunction, roleId: string): Term {
  const known = roleId in ADMIN_SETTINGS_I18N_EN.admin_settings.role_name;
  return known
    ? { label: t(`admin_settings.role_name.${roleId}`), known }
    : { label: roleId, known };
}

export function regionTerm(t: TFunction, region: string): Term {
  const known = region in ADMIN_SETTINGS_I18N_EN.admin_settings.region;
  return known
    ? { label: t(`admin_settings.region.${region}`), known }
    : { label: region, known };
}

/** Audit rows carry raw UUIDs; a full one is noise beside the record's name. */
export function shortRecordId(recordId: string): string {
  return recordId.length <= 14
    ? recordId
    : `${recordId.slice(0, 8)}…${recordId.slice(-4)}`;
}

/**
 * Language and time-zone option labels come from the browser's own CLDR data
 * (Intl), not from our catalogue: "Türkçe (Türkiye)" is localised reference
 * data, and hand-maintaining 400+ time-zone names in two languages would go
 * stale the first time IANA moves a city.
 */
export function languageLabel(locale: string, displayLocale: string): string {
  try {
    const names = new Intl.DisplayNames([displayLocale], { type: "language" });
    return names.of(locale) ?? locale;
  } catch {
    return locale;
  }
}

export function timeZoneOptions(current: string): readonly string[] {
  const supported =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : [];
  // The stored value always stays selectable — an older tenant may hold a zone
  // this browser's CLDR build no longer lists, and dropping it from the options
  // would silently rewrite the setting on the next save.
  return supported.includes(current) || current === ""
    ? supported
    : [current, ...supported];
}

/** Shared by the audit trail and the SSO tab so the two read the same way. */
export function formatTimestamp(timestamp: string, displayLocale: string): string {
  return new Intl.DateTimeFormat(displayLocale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

/**
 * Realm timeouts arrive as raw seconds. Rendered through Intl's unit style
 * rather than a hand-built "30 dk" so plural rules and unit names come from
 * CLDR — Turkish has no plural -s to get wrong, but English does, and one
 * formatter for both beats a locale conditional.
 *
 * Deliberately NOT Intl.DurationFormat: too new to rely on across the browsers
 * and the jsdom build the tests run in.
 */
export function formatDurationSeconds(seconds: number, displayLocale: string): string {
  const [value, unit]: [number, "day" | "hour" | "minute" | "second"] =
    seconds >= 86_400
      ? [Math.round(seconds / 86_400), "day"]
      : seconds >= 3_600
        ? [Math.round(seconds / 3_600), "hour"]
        : seconds >= 60
          ? [Math.round(seconds / 60), "minute"]
          : [seconds, "second"];
  try {
    return new Intl.NumberFormat(displayLocale, {
      style: "unit",
      unit,
      unitDisplay: "long",
    }).format(value);
  } catch {
    return String(seconds);
  }
}

/**
 * Credential ids as Keycloak reports them (`password`, `otp`, `webauthn`, …).
 * Unknown ids render as themselves and badged, same contract as every other
 * term on these screens: an invented name is a bigger lie than a raw code.
 */
export function credentialTerm(t: TFunction, credential: string): Term {
  const known = credential in ADMIN_SETTINGS_I18N_EN.admin_settings.sso_credential;
  return known
    ? { label: t(`admin_settings.sso_credential.${credential}`), known }
    : { label: credential, known };
}
